import { faCheck, faPlus, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { Form, Nav, Tab, Tabs } from "react-bootstrap";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import {
  BroadcastHandlerType,
  selectIsOpenTicket,
} from "../store/slices/broadcastSlice";
import TokenEditor from "../Builder/Components/TokenEditor";
import BroadcastSelectedProjects from "./BroadcastSelectedProjects";
import { createEditorState, shortUUID } from "../helpers";
import { useSelector } from "react-redux";
import MyScroll from "../Common/MyScroll";
import DynamicHeaderInput from "../Builder/nodes/Logic/Inputs/DynamicHeaderInput";
import { useGetCommsSettingsDataQuery } from "../store/slices/settingsSlice";

export function BroadcastHandler({
  handler,
  setHandler,
  deleteHandler,
  isDeleteAllowed = true,
  handlerType = "reply",
  comms,
}: {
  handler: BroadcastHandlerType;
  setHandler: (v: BroadcastHandlerType) => void;
  deleteHandler: () => void;
  isDeleteAllowed?: boolean;
  handlerType?: "reply" | "status" | "inbound";
  comms?: string;
}) {
  const isOpenTicket = useSelector(selectIsOpenTicket);
  const selectedProject = useMemo(
    () => (handler.ProjectId ? [handler.ProjectId] : undefined),
    [handler.ProjectId]
  );
  const { data: commsSettings } = useGetCommsSettingsDataQuery();

  const handleSelectProject = useCallback(
    (p: any) => {
      setHandler({
        ...handler,
        ProjectId: p?.ID,
      });
    },
    [handler, setHandler]
  );

  return (
    <div>
      <div className="mt-2 mb-5 row">
        {handlerType === "reply" && (comms?.split("-").length ?? 0) > 1 && (
          <div className="col-6 mb-3">
            <div className="fw-bold mb-2">Comms channel</div>
            <div className="text-secondary fw-normal">
              Comms channels to handle
            </div>
            <Input
              placeholder="All channels are handled"
              inputType="select"
              isClearable
              value={handler.CommsChannel || []}
              onChange={(v: any) => {
                setHandler({
                  ...handler,
                  CommsChannel: v,
                });
              }}
              options={[
                { value: "sms", label: "SMS" },
                { value: "email", label: "Email" },
                { value: "voice", label: "Voice" },
              ].filter((o) => comms?.split("-").includes(o.value))}
              isMulti={handler.Do !== "auto-reply"}
            />
          </div>
        )}

        {!handler.IsDeliveryStatus ? (
          <div className="col-6 mb-3">
            <div className="fw-bold mb-2">
              Match keywords in the {handlerType}
            </div>
            <div className="text-secondary fw-normal">
              If nothing entered, all{" "}
              {handlerType === "reply" ? "replies" : "inbounds"} will be
              handled.
            </div>
            <Input
              value={handler.Keywords || []}
              onChange={(v: any) => {
                setHandler({
                  ...handler,
                  Keywords: v,
                });
              }}
              options={undefined}
              isMulti
              isValidNewOption={(v) => v.trim().length > 0}
            />
          </div>
        ) : (
          <div className="col-6 mb-3">
            <div className="fw-bold mb-2">Match delivery status:</div>
            <div className="text-secondary fw-normal">
              Select the status type you would like to handle
            </div>
            <Input
              value={handler.DeliveryStatusGroup || []}
              onChange={(v: any) => {
                setHandler({
                  ...handler,
                  DeliveryStatusGroup: v,
                });
              }}
              options={[
                { value: "success", label: "Delivered" },
                { value: "failed", label: "Failed" },
                { value: "sent", label: "Sent" },
                { value: "expired", label: "Expired" },
              ]}
              isMulti
            />
          </div>
        )}

        <div className="col-6 mb-3">
          <div className="fw-bold mb-2">Handler method:</div>
          <div className="text-secondary fw-normal">
            How would you like to handle{" "}
            {handlerType === "inbound" && "inbound message?"}
            {handlerType === "reply" && "reply message?"}
            {handlerType === "status" && "status change?"}
          </div>
          <Form.Select
            aria-label="Handler method"
            value={
              handler.Do === "forward"
                ? handler.Do + ":" + handler.ForwardMethod
                : handler.Do
            }
            onChange={(e: any) => {
              const Do = e.target.value.split(":")[0];
              if (Do === "auto-reply") {
                setHandler({
                  ...handler,
                  Do,
                  CommsChannel: comms?.split("-")[0]
                    ? [comms?.split("-")[0] as any]
                    : [],
                  UseThread: isOpenTicket,
                  ForwardMethod: undefined,
                });
                return;
              }
              if (Do === "forward") {
                const ForwardMethod = e.target.value.split(":")[1];
                if (ForwardMethod === "sms") {
                  setHandler({
                    ...handler,
                    Do,
                    ForwardMethod,
                    UseThread: undefined,
                  });
                  return;
                }
                if (ForwardMethod === "email") {
                  setHandler({
                    ...handler,
                    Do,
                    ForwardMethod,
                    UseThread: undefined,
                    EmailSubject: {
                      EditorState: createEditorState(
                        commsSettings?.EmailSubject || ""
                      ),
                    },
                    EmailFrom: {
                      EditorState: createEditorState(
                        commsSettings?.EmailFrom || ""
                      ),
                    },
                  });
                  return;
                }
              }
              setHandler({
                ...handler,
                Do,
                UseThread: undefined,
                ForwardMethod: undefined,
              });
            }}
          >
            <option value="forward:email">Forward to email:</option>
            <option value="forward:sms">Forward to SMS:</option>
            <option value="run-project">Connect to a component:</option>
            {!handler.IsDeliveryStatus && !(comms && comms === "voice") && (
              <option value="auto-reply">Auto-reply message</option>
            )}
            {handlerType === "inbound" && (
              <option value="no-action">No action</option>
            )}
          </Form.Select>
          {handler.Do === "auto-reply" &&
            comms?.split("-").includes("voice") && (
              <div className="alert alert-warning my-3">
                Please note that voice messages do not support auto-reply
              </div>
            )}
        </div>

        {handler.Do === "auto-reply" && isOpenTicket && (
          <div className="col-auto mb-3">
            <div className="fw-bold">Use thread:</div>
            <div className="text-secondary fw-normal">
              Select thread or dynamic header
            </div>
            <Input
              inputType="radio"
              radioName={shortUUID()}
              value={[!!handler.UseThread]}
              onChange={([v]: any) => {
                setHandler({
                  ...handler,
                  UseThread: v,
                });
              }}
              options={[
                { value: true, label: "Use thread" },
                { value: false, label: "Use dynamic header" },
              ]}
            />
          </div>
        )}

        {((handler.Do === "auto-reply" &&
          (comms === "sms" ||
            handler.CommsChannel?.includes("sms") ||
            (comms?.split("-").includes("sms") &&
              (handler.CommsChannel || []).length === 0))) ||
          (handler.Do === "forward" && handler.ForwardMethod === "sms")) &&
          !handler.UseThread && (
            <div className="mb-3 mt-2 col-6">
              <div className="fw-bold">SMS header:</div>
              <div className="text-secondary fw-normal">
                Select the SMS header
              </div>
              <DynamicHeaderInput
                value={handler.DynamicHeader || ""}
                onChange={(v: any) => {
                  setHandler({
                    ...handler,
                    DynamicHeader: v,
                  });
                }}
                placeholder="Dynamic header"
              />
            </div>
          )}

        {handler.Do === "forward" && handler.ForwardMethod === "email" && (
          <div className="col mb-3 mt-2">
            <div className="fw-bold mb-2">Email address to forward to:</div>
            <Form.Control
              type="email"
              placeholder="type an email address"
              value={handler.ForwardTo}
              onChange={(e) => {
                setHandler({
                  ...handler,
                  ForwardTo: e.target.value,
                });
              }}
            />
          </div>
        )}

        {handler.Do === "forward" && handler.ForwardMethod === "sms" && (
          <div className="col-6 mb-3 mt-2">
            <div className="fw-bold mb-4 ">SMS number to forward to:</div>
            <div>
              <Input
                value={[handler.ForwardTo]}
                onChange={([v]: any) => {
                  setHandler({
                    ...handler,
                    ForwardTo: v,
                  });
                }}
                options={undefined}
                inputType="phone"
              />
            </div>
          </div>
        )}

        {["forward", "auto-reply"].includes(handler.Do) && ( // show message content
          <>
            <div className="col-12 mb-3">
              <div className="fw-bold mb-2 mt-2">Message content:</div>
              <div className="small-editor">
                <TokenEditor
                  inputArgumentsFilter={undefined}
                  inputArguments={undefined}
                  tokenTags={[
                    "shortlinks",
                    "contact_field",
                    ...(handlerType === "reply" ? ["reply-handler"] : []),
                    ...(handlerType === "status" ? ["status-handler"] : []),
                    ...(handlerType === "inbound" ? ["inbound-handler"] : []),
                    // ...(values.IsFileImport
                    //   ? ["imported_contact_field"]
                    //   : []),
                  ]}
                  // showButtons={handler === "email"}
                  rawEditorState={handler.Message.EditorState}
                  onChange={({
                    rawEditorState,
                    HTMLtext,
                    tokens,
                    plainText,
                    links,
                    shortlinks,
                  }: any) => {
                    setHandler({
                      ...handler,
                      Message: { EditorState: rawEditorState },
                    });
                  }}
                  style={undefined}
                  label={undefined}
                  showButtons={undefined}
                  preview={undefined}
                  initialText={undefined}
                />
              </div>
            </div>

            {((handler.Do === "forward" && handler.ForwardMethod === "email") ||
              (handler.Do === "auto-reply" &&
                handler.CommsChannel?.includes("email"))) && (
              <>
                <div className="col-12 mb-3">
                  <div className="fw-bold mb-2">Email subject:</div>
                  <TokenEditor
                    inputArgumentsFilter={undefined}
                    inputArguments={undefined}
                    showShortlinksButton={false}
                    tokenTags={[
                      ...(handlerType === "inbound"
                        ? ["inbound-handler"]
                        : ["broadcast-handler"]),
                      "contact_field",
                      // ...(values.IsFileImport
                      //   ? ["imported_contact_field"]
                      //   : []),
                    ]}
                    // showButtons={handler === "email"}
                    rawEditorState={handler.EmailSubject?.EditorState}
                    onChange={({
                      rawEditorState,
                      HTMLtext,
                      tokens,
                      plainText,
                      links,
                      shortlinks,
                    }: any) => {
                      setHandler({
                        ...handler,
                        EmailSubject: {
                          EditorState: rawEditorState,
                        },
                      });
                    }}
                    style={undefined}
                    label={undefined}
                    showButtons={undefined}
                    preview={undefined}
                    initialText={undefined}
                  />
                </div>
                <div className="col-12 mb-3">
                  <div className="fw-bold mb-2">Email From:</div>
                  <TokenEditor
                    showShortlinksButton={false}
                    inputArgumentsFilter={undefined}
                    inputArguments={undefined}
                    tokenTags={[
                      ...(handlerType === "inbound"
                        ? ["inbound-handler"]
                        : ["broadcast-handler"]),
                      "contact_field",
                      // ...(values.IsFileImport
                      //   ? ["imported_contact_field"]
                      //   : []),
                    ]}
                    // showButtons={handler === "email"}
                    rawEditorState={handler.EmailFrom?.EditorState}
                    onChange={({
                      rawEditorState,
                      HTMLtext,
                      tokens,
                      plainText,
                      links,
                      shortlinks,
                    }: any) => {
                      setHandler({
                        ...handler,
                        EmailFrom: { EditorState: rawEditorState },
                      });
                    }}
                    style={undefined}
                    label={undefined}
                    showButtons={undefined}
                    preview={undefined}
                    initialText={undefined}
                  />
                </div>
              </>
            )}
          </>
        )}

        {handler.Do === "run-project" && (
          <BroadcastSelectedProjects
            selected={selectedProject}
            onSelect={handleSelectProject}
          />
        )}

        {isDeleteAllowed && (
          <div className="col-12 d-flex justify-content-end">
            <button
              aria-label="Delete reply action"
              onClick={deleteHandler}
              type="button"
              className="btn bg-icon p-1 px-2 rounded-pill fw-bold"
            >
              <FontAwesomeIcon icon={faTrashCan} /> DELETE HANDLER
            </button>
          </div>
        )}
      </div>
      <hr />
    </div>
  );
}

export default function BroadcastHandlers({
  handlers,
  setHandlers,
  isDeleteAllowed = true,
  handlerType = "reply",
  comms,
}: {
  handlers: BroadcastHandlerType[];
  setHandlers: any;
  isDeleteAllowed?: boolean;
  handlerType?: "reply" | "status" | "inbound";
  comms?: string;
}) {
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    handlers && (
      <>
        {handlers.map((handler, idx) => (
          <BroadcastHandler
            key={idx}
            handlerType={handlerType}
            handler={handler}
            setHandler={(v: BroadcastHandlerType) => {
              setHandlers(
                handlers?.map((crm: any, i: number) => {
                  if (idx === i) {
                    return v;
                  }
                  return crm;
                })
              );
            }}
            isDeleteAllowed={isDeleteAllowed && handlers.length > 0}
            deleteHandler={() => {
              setHandlers(
                handlers?.filter((_crm: any, i: number) => idx !== i)
              );
              setSelectedTab(0);
            }}
            comms={comms}
          />
        ))}

        {/* <hr />
      <div>
        <div className="fw-bold mt-3">Where should replies be forwarded?</div>
        <div className="text-secondary">
          Lorem ipsum dolor sit amet consectetur ad.
        </div>
        <div className="mt-4">
          <Form.Check // prettier-ignore
            id="reply-sms"
            inline
            label={<div className="fw-bold">SMS</div>}
          />
          <Form.Check // prettier-ignore
            id="reply-email"
            inline
            label={<div className="fw-bold">EMAIL</div>}
          />
          <Form.Check // prettier-ignore
            id="reply-voice"
            inline
            label={<div className="fw-bold">VOICE</div>}
          />
        </div>
      </div> */}
      </>
    )
  );
}
export function BroadcastHandlersTabs({
  handlers,
  setHandlers,
}: {
  handlers: BroadcastHandlerType[];
  setHandlers: any;
}) {
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    handlers && (
      <>
        <Tabs
          activeKey={selectedTab}
          onSelect={(t: any) => setSelectedTab(t)}
          id={shortUUID()}
          className="mb-3"
        >
          {handlers.map((handler, idx) => (
            <Tab
              eventKey={idx}
              title={`Handler (${
                handler.IsDeliveryStatus
                  ? handler.DeliveryStatusGroup?.join(", ") || idx + 1
                  : handler.Keywords?.join(", ") ?? idx + 1
              })`}
            >
              <BroadcastHandler
                key={idx}
                handler={handler}
                setHandler={(v: BroadcastHandlerType) => {
                  setHandlers(
                    handlers?.map((crm: any, i: number) => {
                      if (idx === i) {
                        return v;
                      }
                      return crm;
                    })
                  );
                }}
                deleteHandler={() => {
                  setHandlers(
                    handlers?.filter((_crm: any, i: number) => idx !== i)
                  );
                  setSelectedTab(0);
                }}
                handlerType="status"
              />
            </Tab>
          ))}
        </Tabs>

        {/* <hr />
      <div>
        <div className="fw-bold mt-3">Where should replies be forwarded?</div>
        <div className="text-secondary">
          Lorem ipsum dolor sit amet consectetur ad.
        </div>
        <div className="mt-4">
          <Form.Check // prettier-ignore
            id="reply-sms"
            inline
            label={<div className="fw-bold">SMS</div>}
          />
          <Form.Check // prettier-ignore
            id="reply-email"
            inline
            label={<div className="fw-bold">EMAIL</div>}
          />
          <Form.Check // prettier-ignore
            id="reply-voice"
            inline
            label={<div className="fw-bold">VOICE</div>}
          />
        </div>
      </div> */}
      </>
    )
  );
}
